import { styled } from "@linaria/react";
import { useAvivaSelector } from "@redux/hook";
import { SCREEN_SIZE } from "@static/screen-size";
import { Helpers } from "@utils/helpers";
import React, { forwardRef } from "react";
import { useHistory } from "react-router-dom";
import { ITheme, useTheme } from "src/theme";

const StepperContainerWrapper = styled.div<{ backgroundColor?: string }>`
  background-color: ${(props) => props.backgroundColor || "#FFF"};
  top: 0;
  left: 0;
  right: 0;
  z-index: 10;
  position: fixed;
  @media screen and (min-width: ${SCREEN_SIZE.mobileSteper}px) {
    right: 24px;
    left: unset;
    top: 200px;
  }
`;
const StepperLinkVisit = styled.div<{
  backgroundColor?: string;
  height: string;
}>`
  position: absolute;
  left: 4px;
  right: 4px;
  bottom: 8px;
  height: 2px;
  background-color: ${(props) =>
    props.backgroundColor ? props.backgroundColor : "#fff"};
  z-index: 0;
  @media screen and (min-width: 768px) {
    left: 30px;
    right: 30px;
  }
  @media screen and (min-width: ${SCREEN_SIZE.mobileSteper}px) {
    top: 0;
    height: ${(props) => (props.backgroundColor ? props.height : "0px")};
    right: 12px;
    width: 2px;
    left: unset;
  }
`;

const StepperLink = styled.div<{ backgroundColor?: string }>`
  position: absolute;
  left: 4px;
  right: 4px;
  bottom: 8px;
  height: 2px;
  background-color: ${(props) =>
    props.backgroundColor ? props.backgroundColor : "#f5f5f5"};
  z-index: 0;
  @media screen and (min-width: 768px) {
    left: 30px;
    right: 30px;
  }
  @media screen and (min-width: ${SCREEN_SIZE.mobileSteper}px) {
    top: 0;
    height: 100%;
    right: 12px;
    width: 2px;
    left: unset;
  }
`;

const StepperContainer = styled.div<StepperProps>`
  position: relative;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  width: unset;
  padding: 75px 28px 16px 28px;
  @media screen and (min-width: 768px) {
    padding: 100px 0 16px;
    gap: 24px 0;
    width: 100%;
    max-width: 588px;
    margin: auto;
    z-index: unset;
  }
  @media screen and (min-width: ${SCREEN_SIZE.mobileSteper}px) {
    padding: 75px 16px 16px 16px;
    gap: 24px 0;
    flex-direction: column;
    width: auto;
    padding: 0px;
    width: 100%;
    z-index: unset;
  }
`;

const StepContainer = styled.div<
  Partial<StepItemProps> & { isClickable: boolean }
>`
  cursor: ${(props) => (props.isClickable ? "pointer" : "default")};
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 16px 4px;
  z-index: 1;
  flex: 0 0 65px;
  height: 68px;
  @media screen and (min-width: ${SCREEN_SIZE.mobileSteper}px) {
    flex-direction: row;
    justify-content: flex-end;
    flex: unset;
    height: unset;
  }
`;

const StepText = styled.p<{ active?: boolean }>`
  font-size: 12px;
  color: ${(props) => (props.active ? "#434343" : "#8C8C8C")};
  font-weight: ${(props) => (props.active ? "600" : "400")};
  line-height: 140%;
  max-width: 65px;
  text-align: center;
  margin: auto;
  @media screen and (min-width: ${SCREEN_SIZE.mobileSteper}px) {
    text-align: right;
    max-width: unset;
    font-size: 16px;
    margin: unset;
  }
`;

const StepBullet = styled.div<StepItemProps>`
  margin-top: auto;
  width: 18px;
  height: 18px;
  border-radius: 100rem;
  background-color: ${(props) =>
    props.disabled 
      ? "#F5F5F5" 
      : props.active
      ? "#883E89"
      : props.index < props.activeStepIndex
      ? "#DFC2DD"
      : props.backgroundColor
      ? props.backgroundColor
      : "#F5F5F5"};
  @media screen and (min-width: ${SCREEN_SIZE.mobileSteper}px) {
    width: 24px;
    height: 24px;
  }
`;

const StepInnerContainer = styled.div`
  display: flex;
  margin: auto;
  justify-content: center;
  position: relative;
  gap: 4px;
  @media screen and (min-width: 768px) {
    justify-content: space-between;
    width: 100%;
  }
  @media screen and (min-width: ${SCREEN_SIZE.mobileSteper}px) {
    position: unset;
    display: grid;
    gap: 24px;
    width: unset;
  }
`;

export interface Step {
  label: string;
  isCurrentStep: boolean;
  path?: string;
  id?: string;
  disabled?: boolean
}

export interface StepItemProps {
  active: boolean;
  index: number;
  activeStepIndex: number;
  backgroundColor?: string;
  disabled?: boolean;
}
export interface StepperProps {
  steps: Step[];
  backgroundColor?: string;
  theme?: ITheme;
}

const Stepper = forwardRef<HTMLDivElement, StepperProps>(
  ({ steps, backgroundColor, ...props }: StepperProps, ref) => {
    const theme = useTheme();
    const history = useHistory();
    const campaignDetail = useAvivaSelector(
      (state) => state.loading.campaignDetail
    );
    const analyticUrlParams = useAvivaSelector(
      (state) => state.urlAnalytic.param
    );
    const paramCC = campaignDetail?.campaignCode
      ? `?cc=${campaignDetail.campaignCode}`
      : "";
    const analyticParam = Helpers.getUrlAnalyticParams(
      paramCC !== "",
      analyticUrlParams
    );

    const activeStepIndex = steps.reduce((result, step, index) => {
      if (step.isCurrentStep === true) return (result = index);
      return result;
    }, 0);

    const activeStep = steps.findIndex((step) => step.isCurrentStep === true);

    return (
      <StepperContainerWrapper backgroundColor={backgroundColor}>
        <StepperContainer theme={theme} steps={steps}>
          <StepInnerContainer>
            <StepperLink
              backgroundColor={
                backgroundColor === "none" ? "#FFF" : backgroundColor
              }
            ></StepperLink>
            {steps.map((step, index) => (
              <>
                {backgroundColor && (
                  <StepperLinkVisit
                    backgroundColor={activeStep <= index ? "#DFC2DD" : "#fff"}
                    height={`${activeStep * 12}px`}
                  ></StepperLinkVisit>
                )}
                <StepContainer
                  isClickable={index < activeStepIndex}
                  key={step.label}
                  onClick={() => {
                    if (step?.path && index < activeStepIndex && !step.disabled) {
                      history.push(step.path + paramCC + analyticParam);
                    }
                  }}
                >
                  <StepText active={step.isCurrentStep}>{step.label}</StepText>
                  <StepBullet
                    disabled={step.disabled}
                    active={step.isCurrentStep}
                    index={index}
                    activeStepIndex={activeStepIndex}
                    backgroundColor={
                      backgroundColor === "none" ? "#FFF" : backgroundColor
                    }
                  />
                </StepContainer>
              </>
            ))}
          </StepInnerContainer>
        </StepperContainer>
      </StepperContainerWrapper>
    );
  }
);

Stepper.defaultProps = {
  steps: [],
};
export { Stepper };
